import React, { useState, useEffect } from 'react';
import {
  SafetyAction,
  ActionStatus,
  ActionType,
  ActionPriority,
  SafetyActionDashboardStats,
} from './types';
import { actionService } from './actionService';
import { ActionsTable } from './components/ActionsTable';
import { CreateActionModal } from './components/CreateActionModal';
import { ActionDetailModal } from './components/ActionDetailModal';
import { OverdueAgeingView } from './components/OverdueAgeingView';

type SubmenuView = 'all' | 'my' | 'overdue' | 'verification' | 'effectiveness' | 'closed';

export const ActionsPage: React.FC = () => {
  const [currentView, setCurrentView] = useState<SubmenuView>('all');
  const [actions, setActions] = useState<SafetyAction[]>([]);
  const [stats, setStats] = useState<SafetyActionDashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedType, setSelectedType] = useState<string>('');
  const [selectedPriority, setSelectedPriority] = useState<string>('');
  const [selectedAction, setSelectedAction] = useState<SafetyAction | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);

  const fetchStats = async () => {
    try {
      const data = await actionService.getDashboardStats();
      setStats(data);
    } catch (err: any) {
      console.error('Failed to load action stats:', err);
    }
  };

  const fetchActions = async () => {
    try {
      setLoading(true);
      let statusFilter: ActionStatus[] | undefined = undefined;
      let isOverdueFilter: boolean | undefined = undefined;

      if (currentView === 'verification') {
        statusFilter = ['PENDING_VERIFICATION'];
      } else if (currentView === 'effectiveness') {
        statusFilter = ['EFFECTIVENESS_REVIEW'];
      } else if (currentView === 'closed') {
        statusFilter = ['CLOSED'];
      } else if (currentView === 'overdue') {
        isOverdueFilter = true;
      }

      const res = await actionService.getActions({
        status: statusFilter,
        isOverdue: isOverdueFilter,
        actionType: selectedType ? (selectedType as ActionType) : undefined,
        priority: selectedPriority ? (selectedPriority as ActionPriority) : undefined,
        searchTerm: searchTerm.trim() || undefined,
      });

      setActions(res.items || []);
    } catch (err: any) {
      console.error('Failed to load actions:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  useEffect(() => {
    fetchActions();
  }, [currentView, selectedType, selectedPriority]);

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    fetchActions();
  };

  return (
    <div style={{ maxWidth: '1440px', margin: '0 auto', padding: '0 24px', fontFamily: 'system-ui, -apple-system, sans-serif' }}>
      {/* Top Header */}
      <div className="page-header-banner">
        <div className="badge">SAFETY MANAGEMENT</div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '16px' }}>
          <div>
            <h1>Safety Actions Management</h1>
            <p>Action governance, Segregation of Duties (SoD) verification, ageing tracking, and effectiveness evaluation.</p>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="btn-primary"
            style={{ width: 'auto', padding: '10px 18px' }}
          >
            + Create Safety Action
          </button>
        </div>
      </div>

      {/* KPI Cards */}
      {stats && (
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-label">Total Actions</div>
            <div className="stat-value">{stats?.totalActions || 0}</div>
            <div className="stat-sub">Central register</div>
          </div>

          <div className="stat-card">
            <div className="stat-label">Open Actions</div>
            <div className="stat-value" style={{ color: '#16a34a' }}>{stats?.openActions || 0}</div>
            <div className="stat-sub">Active implementations</div>
          </div>

          <div className="stat-card">
            <div className="stat-label">Overdue Actions</div>
            <div className="stat-value" style={{ color: '#dc2626' }}>{stats?.overdueActions || 0}</div>
            <div className="stat-sub">Past target date</div>
          </div>

          <div className="stat-card">
            <div className="stat-label">Pending Verification</div>
            <div className="stat-value" style={{ color: '#c026d3' }}>{stats?.pendingVerificationCount || 0}</div>
            <div className="stat-sub">Dual-signoff check</div>
          </div>

          <div className="stat-card">
            <div className="stat-label">Effectiveness Review</div>
            <div className="stat-value" style={{ color: '#d97706' }}>{stats?.effectivenessReviewCount || 0}</div>
            <div className="stat-sub">Post-implementation</div>
          </div>

          <div className="stat-card">
            <div className="stat-label">Completed (Month)</div>
            <div className="stat-value" style={{ color: '#0f172a' }}>{stats?.completedThisMonth || 0}</div>
            <div className="stat-sub">Closed successfully</div>
          </div>
        </div>
      )}

      {/* Submenu Navigation */}
      <div className="nav-tabs">
        {[
          { id: 'all', label: 'All Actions' },
          { id: 'overdue', label: `Overdue & Ageing (${stats?.overdueActions || 0})` },
          { id: 'verification', label: `Pending Verification (${stats?.pendingVerificationCount || 0})` },
          { id: 'effectiveness', label: `Effectiveness Reviews (${stats?.effectivenessReviewCount || 0})` },
          { id: 'closed', label: 'Closed Actions' },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setCurrentView(tab.id as SubmenuView)}
            className={`tab-btn ${currentView === tab.id ? 'active' : ''}`}
          >
            {tab.label}
          </button>
        ))}
      </div>

        {/* Filter Bar */}
        <form onSubmit={handleSearchSubmit} style={{ display: 'flex', gap: '8px', alignItems: 'center', paddingBottom: '6px' }}>
          <input
            type="text"
            placeholder="Search ref or title..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              padding: '6px 12px',
              fontSize: '12px',
              borderRadius: '6px',
              border: '1px solid #cbd5e1',
              width: '200px',
            }}
          />

          <select
            value={selectedType}
            onChange={(e) => setSelectedType(e.target.value)}
            style={{
              padding: '6px 10px',
              fontSize: '12px',
              borderRadius: '6px',
              border: '1px solid #cbd5e1',
              background: '#ffffff',
            }}
          >
            <option value="">All Action Types</option>
            <option value="CORRECTIVE">Corrective</option>
            <option value="PREVENTIVE">Preventive</option>
            <option value="CONTAINMENT">Containment</option>
          </select>

          <select
            value={selectedPriority}
            onChange={(e) => setSelectedPriority(e.target.value)}
            style={{
              padding: '6px 10px',
              fontSize: '12px',
              borderRadius: '6px',
              border: '1px solid #cbd5e1',
              background: '#ffffff',
            }}
          >
            <option value="">All Priorities</option>
            <option value="IMMEDIATE">Immediate</option>
            <option value="HIGH">High</option>
            <option value="MEDIUM">Medium</option>
            <option value="LOW">Low</option>
          </select>

          <button
            type="submit"
            style={{
              padding: '6px 12px',
              fontSize: '12px',
              fontWeight: 600,
              borderRadius: '6px',
              border: 'none',
              background: '#0284c7',
              color: '#ffffff',
              cursor: 'pointer',
            }}
          >
            Filter
          </button>
        </form>

      {/* Main View Body */}
      {currentView === 'overdue' ? (
        <OverdueAgeingView
          actions={actions}
          stats={stats}
          onSelect={(a) => setSelectedAction(a)}
        />
      ) : (
        <ActionsTable
          actions={actions}
          loading={loading}
          onSelect={(a) => setSelectedAction(a)}
          onRefresh={() => {
            fetchActions();
            fetchStats();
          }}
        />
      )}

      {/* Modals */}
      {showCreateModal && (
        <CreateActionModal
          onClose={() => setShowCreateModal(false)}
          onCreated={() => {
            setShowCreateModal(false);
            fetchActions();
            fetchStats();
          }}
        />
      )}

      {selectedAction && (
        <ActionDetailModal
          actionId={selectedAction.id}
          onClose={() => setSelectedAction(null)}
          onUpdated={() => {
            fetchActions();
            fetchStats();
          }}
        />
      )}
    </div>
  );
};
